import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { ActionsExecuting, actionsExecuting } from '@ngxs-labs/actions-executing';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { GetCatalogInfo, RefreshCurrentCatalogStats, SetSelectCatalog } from '@shared/state/catalog/shared-catalog.action';
import { catchError, delay, switchMap, take, tap } from 'rxjs/operators';
import { CatalogParams, CatalogResponse, ReleaseStatus, ResponseCatalogDataSave } from '@shared/models/catalog.service';
import { Login } from '@auth/store/auth/auth.action';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AuthState } from '@auth/store/auth/auth.state';
import { GetRecommendationCatalogItemsForUser, ShowLayout } from '@shared/state/shared/shared.action';
import { CatalogService } from '@shared/services/catalog.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { convertCatalogResponseToView } from '@utils/converters';
import { CatalogItemService } from '../../services/catalog-item.service';
import { CatalogStateType } from '../../models/catalog-item.service';
import {
  GetCatalogsExportStatus,
  GetCurrentCatalogVersionRelease,
  SetCurrentCatalogVersionRelease
} from '../../../../store/catalog/catalog.action';
import { LoadingCatalogVersionRelease } from '../../../../models/catalog';
import { CatalogState } from '../../../../store/catalog/catalog.state';
import { CatalogItemsQueryParams } from '../../models/catalog-item-component';
import {
  ChangeCatalogItem,
  ChangedCatalogItemsFilter,
  ChangedCatalogItemsIsRecommendation
} from '../../store/catalog-items.actions';
import { GetNotificationCatalogs } from '../../../../store/catalog-change-notifications/catalog-change-notifications.action';
import { GetFavoriteCatalogs } from '../../../../store/favorite-catalogs/favorite-catalogs.action';
import { CustomIcons } from '@shared/models/custom-icons';
import { CatalogItemsDataService } from '../catalog-items/catalog-items-data.service';

@Component({
  selector: 'app-catalog-items-data-main',
  templateUrl: './catalog-items-data-main.component.html',
  styleUrls: ['./catalog-items-data-main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogItemsDataMainComponent implements OnInit, OnDestroy {
  @Select(actionsExecuting([GetNotificationCatalogs, GetFavoriteCatalogs, Login, GetCurrentCatalogVersionRelease, GetCatalogInfo]))
  public loadingData$: Observable<ActionsExecuting>;
  public catalogVersionRelease$: Observable<LoadingCatalogVersionRelease | null>;
  public loadingAllData = false;
  public catalogStatistic$: Observable<ResponseCatalogDataSave>;
  public breakPointObserve$ = this.breakpointObserver
    .observe([
      '(min-width: 800px)',
      '(min-width: 1025px)',
      '(max-width: 1110px)',
      '(max-width: 1310px)',
      '(max-width: 1340px)',
    ])
    .pipe(
      tap(state => {
        if (state.breakpoints['(min-width: 1025px)']) {
          this.store.dispatch(new ShowLayout(true));
          this.document.documentElement.style.setProperty('--toolbar-height', '75px');
        } else {
          this.store.dispatch(new ShowLayout(false));
          this.document.documentElement.style.setProperty('--toolbar-height', '0px');
        }
      })
    );
  public isError$ = new BehaviorSubject<any>(null);
  public icons = CustomIcons;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private activeRoute: ActivatedRoute,
    private store: Store,
    private catalogItemService: CatalogItemService,
    private router: Router,
    private catalogService: CatalogService,
    private catalogItemsDataService: CatalogItemsDataService,
    private breakpointObserver: BreakpointObserver,
    @Inject(DOCUMENT) private document: Document
  ) {}

  private getCatalogInfo(catalogId: number): void {
    const isDynamic = (this.activeRoute.snapshot.queryParams as CatalogItemsQueryParams)?.isDynamic === 'true';
    if (catalogId) {
      this.store.dispatch([new GetCatalogInfo(+catalogId, isDynamic)]);
      let hasSave = false;
      if (isPlatformBrowser(this.platformId)) {
        const hasSaveString = sessionStorage.getItem(`${catalogId}saveCatalogStatistic`);
        if (hasSaveString) {
          hasSave = JSON.parse(hasSaveString);
        }
        const catalogVersion = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease);
        const isNotPublicCatalog = catalogVersion?.versionReleaseInfo?.releaseStatus === ReleaseStatus.New;
        if (!hasSave && !isNotPublicCatalog) {
          this.catalogStatistic$ = of(1).pipe(
            take(1),
            delay(5000),
            switchMap(() => {
              if (isPlatformBrowser(this.platformId)) {
                sessionStorage.setItem(`${catalogId}saveCatalogStatistic`, JSON.stringify(true));
              }
              return this.catalogItemService
                .saveCatalogStatistic({
                  idOpenCatalog: +catalogId,
                  type: CatalogStateType.CheckCatalog
                })
                .pipe(
                  tap(() => {
                    this.store.dispatch(new RefreshCurrentCatalogStats());
                  })
                );
            })
          );
        }
      }
    }
    this.loadingAllData = true;
  }

  public ngOnInit(): void {
    const params = this.activeRoute.snapshot.queryParams;
    const catalogId = this.activeRoute.snapshot.paramMap.get('id');
    if (catalogId) {
      let catalogParam: CatalogParams = {
        idDataset: +catalogId,
        offset: 0,
        limit: 1,
        criteria: 'isDynamic = false'
      };

      if (isNaN(+catalogId)) {
        catalogParam.idDataset = undefined;
        catalogParam.criteria = `sefUrl = '${catalogId}'`;
      }

      if (params.isDynamic && params.isDynamic === 'true') {
        catalogParam = {
          ...catalogParam,
          criteria: 'isDynamic = true'
        };
      }
      if (params.access) {
        catalogParam = {
          ...catalogParam,
          access: params.access
        };
      }

      this.catalogService
        .getCatalogs(catalogParam)
        .pipe(
          catchError((err) => {
            // this.router.navigate([`error`]);
            console.log('Im DOWNLOADING LIST ERROR from catchError', err);
            this.isError$.next(true);
            return of({
              response: [],
              found: 0
            } as CatalogResponse);
          }),
          take(1)
        )
        .subscribe((res: CatalogResponse) => {
          if (res.response.length === 0) {
            console.log('Im DOWNLOADING LIST ERROR');
            this.isError$.next(res);
            this.router.navigate([`error`]);
          } else if (res.response.length > 0 && res.response[0].idPublicCatalog) {
            const selectCatalog = convertCatalogResponseToView(res.response[0]);
            this.store.dispatch(new SetSelectCatalog(selectCatalog));
            const idPublicCatalog = res.response[0].idPublicCatalog;

            const user = this.store.selectSnapshot(AuthState.authenticatedUser);
            if (user && user.token) {
              if (params.isRecommendationData) {
                const isRecommendationData: boolean = params.isRecommendationData.includes('true');
                this.store.dispatch(new ChangedCatalogItemsIsRecommendation(isRecommendationData, +idPublicCatalog, false));
                this.store.dispatch(new GetRecommendationCatalogItemsForUser(+idPublicCatalog));
              }
              this.store.dispatch([new GetNotificationCatalogs(), new GetFavoriteCatalogs()]);
            }

            if (idPublicCatalog && ((params.isDynamic && params.isDynamic !== 'true') || !params.isDynamic)) {
              this.catalogVersionRelease$ = this.store.select(CatalogState.currentCatalogVersionRelease).pipe(
                tap(versionRelease => {
                  if (versionRelease && versionRelease.catalogId === +idPublicCatalog && versionRelease.versionReleaseInfo === null) {
                    this.router.navigate(['error']);
                  }
                })
              );
              if (!params?.version) {
                this.loadingAllData = true;
                if (selectCatalog) {
                  this.store.dispatch(
                    new SetCurrentCatalogVersionRelease(selectCatalog.id, {
                      catalogId: selectCatalog.id,
                      releaseNum: selectCatalog.actualReleaseNum,
                      versionNum: selectCatalog.actualVersionNum,
                      releaseDate: selectCatalog.actualRelease,
                      versionDate: selectCatalog.actualVersion,
                      releaseStatus: selectCatalog.actualVersion ? ReleaseStatus.Actual : ReleaseStatus.New,
                      releaseDateFull: '',
                      defaultDate: '',
                      countRows: selectCatalog.countRows
                    })
                  );
                  this.store.dispatch(new GetCatalogsExportStatus([+idPublicCatalog], selectCatalog.actualRelease));
                  this.getCatalogInfo(idPublicCatalog);
                }
              } else if (params?.version && params?.release) {
                this.loadingAllData = false;
                this.store
                  .dispatch(
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    new GetCurrentCatalogVersionRelease(
                      +catalogId,
                      true,
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                      params.access ?? null,
                      +params.version ?? null,
                      +params.release ?? null
                    )
                  )
                  .pipe(take(1))
                  .subscribe(() => {
                    const catalogVersion = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease);
                    if (catalogVersion && catalogVersion?.versionReleaseInfo?.versionDate) {
                      this.store.dispatch(new GetCatalogsExportStatus([+idPublicCatalog], catalogVersion.versionReleaseInfo.releaseDate));
                    }

                    if (!this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease)) {
                      this.router.navigate(['error']);
                    }

                    this.getCatalogInfo(catalogVersion?.versionReleaseInfo?.catalogId ?? idPublicCatalog);
                  });
              }
            } else {
              this.store.dispatch(
                new SetCurrentCatalogVersionRelease(selectCatalog.id, {
                  catalogId: selectCatalog.id,
                  releaseNum: selectCatalog.actualReleaseNum,
                  versionNum: selectCatalog.actualVersionNum,
                  releaseDate: selectCatalog.actualRelease,
                  versionDate: selectCatalog.actualVersion,
                  releaseStatus: selectCatalog.actualVersion ? ReleaseStatus.Actual : ReleaseStatus.New,
                  releaseDateFull: '',
                  defaultDate: '',
                  countRows: selectCatalog.countRows
                })
              );
              this.getCatalogInfo(idPublicCatalog);
            }
          } else {
            this.router.navigate(['error']);
          }
        });
    }
  }

  public ngOnDestroy(): void {
    this.document.documentElement.style.setProperty('--toolbar-height', '75px');
    this.catalogItemsDataService.isAllSelected.next(false);
    this.catalogItemsDataService.selectColumns.next(null);
    this.store.dispatch(new ShowLayout(true));
    this.store.dispatch(new ChangedCatalogItemsFilter('', true));
    this.store.dispatch(new ChangeCatalogItem(null));
  }
}
